import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addWrapper,
  flowMax,
} from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import { compact } from 'lodash/fp'
import React, { FC } from 'react'
import { isMobile } from 'react-device-detect'
import { Helmet } from 'react-helmet-async'
import { createUseStyles } from 'react-jss'
import { Product as SchemaProduct, WithContext } from 'schema-dts'
import { addApi } from 'shared/src/api'
import {
  FeedImageContent,
  FeedMedia,
  ProductDiscoverySource,
  ProductWithImage,
  User,
  isProductDiscoverySource,
} from 'shared/src/api/types'
import {
  addIntPathParam,
  addNavigate,
  addOptionalIntQueryParam,
  addOptionalQueryParam,
} from 'shared/src/utils/addNavigate'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { trackEvent, trackProductView } from 'shared/src/utils/analytics'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { formattedUsd } from 'shared/src/utils/formatting'
import { typedAs } from 'shared/src/utils/typedAs'
import { Link } from 'react-router-dom'

import { ReactComponent as BackIcon } from '../../../../assets/chevron-left-slim.svg'
import productExplore from 'assets/product-explore.svg'
import { Button } from 'components/Button'
import { LinkOrButton } from 'components/LinkOrButton'
import { addLaunchContext } from 'utils/launchContext'
import { setPageTitle } from 'utils/title'
import { addLoadingAndErrorIndicators } from '../../../../utils/addLoadingAndErrorIndicators'
import { addTranslationHelpers } from '../../../../utils/addTranslationHelpers'
import { constrainWidth } from '../../../../utils/layout'
import { makeMediaUrl } from '../../../../utils/routes'
import { sansSerifFontFamily, serifFontFamily } from '../../../../utils/styles'
import {
  PRODUCT_DETAIL_ABOVE_IMAGE_Z_INDEX,
  PRODUCT_DETAIL_IMAGE_Z_INDEX,
} from '../../../../utils/zIndex'
import { BookmarkButton } from '../../../BookmarkButton'
import { WhiteButton } from '../../../WhiteButton'
import { addMenuPage } from '../../../menu'
import { addRegisterPage } from '../../../register'
import { RelatedProducts } from './RelatedProducts'

const makeSchemaProduct = (
  product: ProductWithImage
): WithContext<SchemaProduct> =>
  ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    brand: {
      '@type': 'Brand',
      name: product.brandName,
    },
    name: product.name,
    image: product.image.full,
    offers: compact([
      product.price !== null
        ? {
            '@type': 'Offer',
            price: product.price / 100,
            priceCurrency: 'USD',
          }
        : null,
    ]),
  } as const)

const makeProductUrl = (
  urlString: string,
  {
    productId,
    mediaId,
    userListId,
  }: {
    productId: number
    mediaId?: number
    userListId?: number
    user?: User
  }
): string => {
  const url = new URL(urlString)

  url.searchParams.append('_rodeo_product_id', productId.toString())

  if (mediaId) {
    url.searchParams.append('_rodeo_media_id', mediaId.toString())
  }
  if (userListId) {
    url.searchParams.append('_rodeo_user_list_id', userListId.toString())
  }

  return url.toString()
}

const BLUR_RADIUS = 10

const useStyles = createUseStyles({
  container: {
    backgroundColor: '#101010',
    minHeight: '100%',
    position: 'relative',
    paddingBottom: 5,
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: PRODUCT_DETAIL_ABOVE_IMAGE_Z_INDEX,
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 18,
    paddingBottom: 40,
  },
  imageContainer: {
    width: '100%',
    background: 'none',
    border: 'none',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  blurredImage: {
    position: 'absolute',
    top: -2 * BLUR_RADIUS,
    left: -2 * BLUR_RADIUS,
    width: `calc(100% + 4 * ${BLUR_RADIUS}px)`,
    height: `calc(100% + 4 * ${BLUR_RADIUS}px)`,
    filter: `blur(${BLUR_RADIUS}px)`,
    objectFit: 'cover',
    backgroundColor: '#ffffff',
  },
  image: {
    height: '60dvh',
    maxWidth: '100%',
    objectFit: 'contain',
    zIndex: PRODUCT_DETAIL_IMAGE_Z_INDEX,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
  },
  shopButton: {
    flexGrow: 1,
    flex: 1,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: sansSerifFontFamily,
    fontWeight: 500,
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.6)',
  },
  namePriceContainer: {
    marginBottom: 40,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  name: {
    margin: 0,
    fontWeight: 600,
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  price: {
    margin: 0,
    fontFamily: sansSerifFontFamily,
    fontWeight: 700,
    fontSize: 16,
    color: 'white',
    minWidth: 60,
    overflow: 'clip',
    textAlign: 'right',
  },
  featuredProductsTitle: {
    fontFamily: serifFontFamily,
    fontWeight: 500,
    fontSize: 18,
    color: 'white',
    marginTop: 0,
  },
  featuredProductsLasso: {
    marginBottom: 50,
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginLeft: 24,
    marginRight: 24,
    marginBottom: 8,
  },
  viewAllText: {
    color: '#CBA67A',
    textDecoration: 'none',
    fontSize: 12,
    fontFamily: sansSerifFontFamily,
    fontWeight: 500,
    lineHeight: '16px',
    alignSelf: 'center',
    paddingTop: 3,
  },
  brandProductsContainer: {
    display: 'flex',
    gap: 11,
    overflowX: 'scroll',
    marginLeft: 24,
    marginBottom: 42,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  mediasContainer: {
    display: 'flex',
    gap: 11,
    overflowX: 'scroll',
    marginLeft: 24,
    marginBottom: 42,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  mediaImage: {
    cursor: 'pointer',
    position: 'relative',
    height: 160,
    width: 120,
    objectFit: 'contain',
  },
  mediasTitle: {
    fontFamily: serifFontFamily,
    fontWeight: 500,
    fontSize: 16,
    color: 'white',
    marginTop: 0,
  },
  lassoTitle: {
    margin: 0,
    marginBottom: 100,
    marginLeft: 24,
  },
  rightBar: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    position: 'absolute',
    right: 24,
    top: 32,
    bottom: 36,
    pointerEvents: 'none',
  },
  rightBarTop: {
    paddingLeft: 10,
  },
  rightBarInner: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'auto',
    zIndex: PRODUCT_DETAIL_ABOVE_IMAGE_Z_INDEX,
  },
  featuredCreatorsHeader: {
    fontFamily: serifFontFamily,
    fontWeight: 500,
    fontSize: 18,
    color: 'white',
    marginBottom: 32,
    marginLeft: 24,
  },
  affiliateDisclosure: {
    fontFamily: 'sans-serif',
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    marginTop: 26,
    marginLeft: 26,
    marginBottom: 55,
  },
})

export const ProductDetail: FC = flowMax(
  addDisplayName('ProductDetail'),
  constrainWidth({ fillHeight: true }),
  addIntPathParam('productId'),
  addOptionalIntQueryParam('mediaId'),
  addOptionalIntQueryParam('userListId'),
  addOptionalQueryParam('creator'),
  addNavigationState(
    'discoverySource',
    'setDiscoverySource',
    typedAs<ProductDiscoverySource | undefined>(undefined)
  ),
  addWrapper((render, { productId }) => (
    <React.Fragment key={productId}>{render()}</React.Fragment>
  )),
  addApi,
  addLaunchContext,
  addState('imgSrc', 'setImgSrc', ''),
  addState(
    'hasSeenInitialRecommendations',
    'setSeenInitialRecommendations',
    false
  ),
  addEffectOnMount(({ setSeenInitialRecommendations }) => () => {
    setSeenInitialRecommendations(true)
  }),
  addDataLoadingFromPromise(
    'productDetails',
    ({ productId, mediaId, userListId, api }) =>
      api.products.details(productId, {
        mediaId,
        userListId,
      }),
    ['productId', 'mediaId'],
    ({ productId, mediaId }) => `productDetails-${productId}-${mediaId}`
  ),
  addDataLoadingFromPromise(
    'topMedias',
    ({ productId, mediaId, api }) => api.products.medias(productId, mediaId),
    ['productId'],
    ({ productId, mediaId }) =>
      `productDetails-topMedias-${productId}-${mediaId}`
  ),
  addDataLoadingFromPromise(
    'trendingMedias',
    ({ api }) => api.tags.paginatedFeed(1, 0, null),
    [],
    ({ productId, mediaId }) =>
      `productDetails-trending-${productId}-${mediaId}`
  ),
  addDataLoadingFromPromise(
    'user',
    ({ creator, api }) =>
      creator ? api.users.details(creator) : Promise.resolve(null),
    ['creator'],
    ({ creator }) => `productDetails-users-details-${creator}`
  ),
  addLoadingAndErrorIndicators('productDetails', {
    redirectTo404IfError: true,
  }),
  addLoadingAndErrorIndicators('user', {
    redirectTo404IfError: true,
  }),
  addLoadingAndErrorIndicators('topMedias', {
    redirectTo404IfError: true,
  }),
  addLoadingAndErrorIndicators('trendingMedias', {
    redirectTo404IfError: true,
  }),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  addProps(({ productDetails: { product } }) => ({
    product,
  })),
  addProps(({ product: { brandName, name }, t }) => ({
    title: t('productDetail.title', { brand: brandName, name }),
  })),
  setPageTitle(({ title }) => title),
  addProps(({ product }) => ({
    schemaOrg: JSON.stringify(makeSchemaProduct(product)),
  })),
  addEffectOnMount(
    ({
        productId,
        mediaId,
        userListId,
        user,
        discoverySource,
        api,
        product: { image },
        setImgSrc,
      }) =>
      () => {
        setImgSrc(image.lowres)
        trackProductView(
          {
            productId,
            mediaId,
            userListId,
            creatorId: user?.id,
            discoverySource: isProductDiscoverySource(discoverySource)
              ? discoverySource
              : undefined,
          },
          api
        )
      }
  ),
  addProps(({ creator, mediaId }) => ({
    origin:
      creator && mediaId
        ? {
            creatorUsername: creator,
            mediaId,
          }
        : null,
  })),
  addNavigate,
  addMenuPage,
  addRegisterPage,
  addState('isShuffling', 'setShuffling', false),
  addHandlers({
    onShop:
      ({
        mediaId,
        userListId,
        product: { id, url },
        user,
        discoverySource,
        api,
      }) =>
      async () => {
        const createOutgoingProductClickPromise = api.analytics
          .createOutgoingProductClick({
            mediaId,
            userListId,
            productId: id,
            discoverySource: isProductDiscoverySource(discoverySource)
              ? discoverySource
              : undefined,
          })
          .catch((error) =>
            console.error('Error creating outgoing product click', error)
          )

        trackEvent(
          {
            category: 'product',
            action: 'shop',
          },
          {
            media_id: mediaId,
            user_list_id: userListId,
            product_id: id,
            creator_id: user?.id,
            url,
          }
        )
        if (isMobile) {
          await createOutgoingProductClickPromise
          window.location.href = url
        } else {
          window.open(url, '_blank')
        }
      },
  }),
  addProps(
    ({
      product: { id, url: productUrl, originalUrl },
      mediaId,
      userListId,
      user,
    }) => ({
      productUrl: makeProductUrl(originalUrl ?? productUrl, {
        productId: id,
        mediaId,
        userListId,
        user: user ?? undefined,
      }),
    })
  ),
  addProps(({ topMedias }) => ({
    hasTopMedias: topMedias.length > 0,
  })),
  addProps(({ user }) => ({
    isStealthMode: user?.isStealth,
  })),
  addTranslationHelpers,
  ({
    product: { id, image, brandName, name, price },
    schemaOrg,
    mediaId,
    userListId,
    user,
    hasTopMedias,
    topMedias,
    trendingMedias,
    onShop,
    classes,
    imgSrc,
    setImgSrc,
    t,
  }) => (
    <>
      <Helmet>
        <meta property="og:type" content="product" />
        <meta property="og:image" content={image.full} />
        {price && (
          <meta property="product:price:amount" content={formattedUsd(price)} />
        )}
        {price && <meta property="product:price:currency" content="USD" />}
        <script type="application/ld+json">{schemaOrg}</script>
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_SHOPPER_URL}/products/${id}`.replace(
            /(?<!:)\/+/g,
            '/'
          )}
        />
      </Helmet>
      <div className={classes.container}>
        <button
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            width: 44,
            height: 44,
            zIndex: 1000,
            backgroundColor: '#1010104D',
            borderRadius: '8px',
            border: 'none',
            backdropFilter: 'blur(15px)',
            cursor: 'pointer',
          }}
          onClick={() => window.history.back()}
        >
          <BackIcon />
        </button>
        <button onClick={onShop} className={classes.imageContainer}>
          <img src={image.lowres} alt="" className={classes.blurredImage} />
          <img
            src={imgSrc}
            onLoad={() => setImgSrc(image.full)}
            alt={t('productDetail.imageAlt', { brand: brandName, name })}
            className={classes.image}
          />
        </button>
        <div className={classes.infoContainer}>
          <div className={classes.brand}>
            <div>{brandName}</div>
            <Button onClick={onShop}>
              <img
                src={productExplore}
                alt={
                  price != null ? 'productDetail.shop' : 'productDetail.explore'
                }
              />
            </Button>
          </div>
          <div className={classes.namePriceContainer}>
            <p className={classes.name}>{name}</p>
            {price && <p className={classes.price}>{formattedUsd(price)}</p>}
          </div>
          <div className={classes.buttonContainer}>
            <BookmarkButton
              mediaId={mediaId}
              userListId={userListId}
              creatorId={user?.id}
              productId={id}
              roundupStyle={'mini'}
            />
            <WhiteButton
              title={t(
                price != null ? 'productDetail.shop' : 'productDetail.explore'
              )}
              onClick={onShop}
              className={classes.shopButton}
            />
          </div>
        </div>
        {hasTopMedias && (
          <>
            <div className={classes.titleContainer}>
              <p className={classes.mediasTitle}>Also seen on</p>
            </div>
            <div className={classes.mediasContainer}>
              {topMedias.map((media) => (
                <React.Fragment key={media.id}>
                  <Link to={makeMediaUrl({ mediaId: media.id })}>
                    <img
                      src={(media.content as FeedImageContent).value.thumbnail}
                      alt={`${media.user.username}'s ${brandName}  ${name}`}
                      className={classes.mediaImage}
                    />
                  </Link>
                </React.Fragment>
              ))}
            </div>
          </>
        )}
        <>
          <div className={classes.titleContainer}>
            <p className={classes.mediasTitle}>Trending on rodeo</p>
          </div>
          <div className={classes.mediasContainer}>
            {trendingMedias.data.media.map((media) => {
              if (media.type === 'media') {
                const feedMedia = media as FeedMedia
                if (feedMedia.content.type === 'image') {
                  return (
                    <React.Fragment key={feedMedia.id}>
                      <Link to={makeMediaUrl({ mediaId: feedMedia.id })}>
                        <img
                          src={
                            (feedMedia.content as FeedImageContent).value
                              .thumbnail
                          }
                          alt={`${feedMedia.user.username}'s ${brandName}  ${name}`}
                          className={classes.mediaImage}
                        />
                      </Link>
                    </React.Fragment>
                  )
                }
              }
              return <></>
            })}
          </div>
        </>
        <div className={classes.affiliateDisclosure}>
          {t('affiliateDisclosure')}
        </div>
        {!hasTopMedias && <RelatedProducts productId={id} user={user} />}
        <div
          style={{
            borderRadius: '24px 24px 0 0 ',
            backgroundColor: '#272727',
            padding: '34px 24px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <p
            style={{
              color: '#FFF',
              fontFamily: 'Playfair Display, serif',
              fontSize: 24,
              fontWeight: 600,
              margin: 0,
            }}
          >
            Explore rod<i>e</i>o
          </p>
          <LinkOrButton
            navigateTo={'/'}
            showBackButton
            className={classes.viewAllText}
          >
            view all
          </LinkOrButton>
        </div>
      </div>
    </>
  )
)
